import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import Badge from "@react/shared/ui/Badge"
import { postRequest } from "@react/helpers/fetch"
import { dateFmt } from "../helpers/format"

const t = (key, opts) => I18n.t(`projects.equipments.${key}`, opts)

const LABEL  = "block text-xs font-medium text-slate-700 mb-1.5"
const INPUT  = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400"
const SELECT = `${INPUT} bg-white`
const WARRANTIES = [0, 6, 12, 24, 36, 48, 60]

const Kpi = ({ label, value, tone = "text-slate-900" }) => (
  <div className="px-4 py-3 bg-white border border-slate-200 rounded-xl">
    <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">{label}</div>
    <div className={`text-[17px] font-semibold tabular-nums ${tone}`}>{value}</div>
  </div>
)

const InstallModal = ({ row, equipments, onClose, onSaved }) => {
  const today     = new Date().toISOString().slice(0, 10)
  const remaining = Math.max(1, Math.round((row.quantity || 1) - (row.installed_count || 0)))
  const [units, setUnits]     = useState(Array.from({ length: remaining }, () => ({ serial_number: "", name: "" })))
  const [date, setDate]       = useState(today)
  const [warranty, setWarranty] = useState(24)
  const [createWo, setCreateWo] = useState(true)
  const [technicianId, setTechnicianId] = useState("")
  const [datePlanned, setDatePlanned]   = useState(today)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)

  const setUnit = (index, patch) => setUnits(previous => previous.map((unit, i) => (i === index ? { ...unit, ...patch } : unit)))
  const setCount = count => {
    const size = Math.max(1, Math.min(50, parseInt(count, 10) || 1))
    setUnits(previous => Array.from({ length: size }, (_, i) => previous[i] || { serial_number: "", name: "" }))
  }

  const submit = () => {
    setSaving(true); setError(null)
    postRequest({
      url:  equipments.create_url,
      body: { qline_id: row.qline_id, units, in_service_date: date, warranty_months: warranty, create_workorder: createWo, technician_id: technicianId || null, date_planned: datePlanned },
    })
      .then(response => {
        if (response?.errors) { setError(response.errors.join(", ")); setSaving(false); return }
        onSaved(response); onClose()
      })
      .catch(() => { setError(I18n.t("projects.engagements.form.errors.network")); setSaving(false) })
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="md" onClick={onClose} disabled={saving}>{t("modal.cancel")}</Button>
        <Button variant="primary" size="md" onClick={submit} disabled={saving}>
          {saving && <FontAwesomeIcon icon="circle-notch" spin className="mr-1.5" />}{t("modal.save")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal isOpen onClose={onClose} eyebrow={t("modal.eyebrow")} title={t("modal.title", { name: row.product?.name || row.description })} size="lg" footer={footer}>
      <p className="text-[12.5px] text-slate-500 mb-4">{t("modal.hint")}</p>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <label className={LABEL}>{t("modal.units")}</label>
          <input type="number" min="1" max="50" className={INPUT} value={units.length} onChange={event => setCount(event.target.value)} />
        </div>
        <div className="col-span-4">
          <label className={LABEL}>{t("modal.in_service_date")}</label>
          <input type="date" className={INPUT} value={date} onChange={event => setDate(event.target.value)} />
        </div>
        <div className="col-span-4">
          <label className={LABEL}>{t("modal.warranty")}</label>
          <select className={SELECT} value={warranty} onChange={event => setWarranty(parseInt(event.target.value, 10))}>
            {WARRANTIES.map(months => <option key={months} value={months}>{months === 0 ? t("modal.warranty_none") : t("modal.warranty_months", { count: months })}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 bg-slate-50/70 border border-slate-200 rounded-xl divide-y divide-slate-100">
        {units.map((unit, index) => (
          <div key={index} className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)] gap-3 items-center px-3 py-2">
            <span className="text-[11px] font-semibold text-slate-400 tabular-nums">{index + 1}</span>
            <input className={INPUT} placeholder={t("modal.serial")} value={unit.serial_number} onChange={event => setUnit(index, { serial_number: event.target.value })} />
            <input className={INPUT} placeholder={t("modal.unit_name")} value={unit.name} onChange={event => setUnit(index, { name: event.target.value })} />
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 p-4">
        <label className="flex items-center gap-2 text-[13px] text-slate-800 font-medium">
          <input type="checkbox" className="h-4 w-4 rounded border border-slate-300" checked={createWo} onChange={event => setCreateWo(event.target.checked)} />
          {t("modal.create_workorder")}
        </label>
        {createWo && (
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <label className={LABEL}>{t("modal.technician")}</label>
              <select className={SELECT} value={technicianId} onChange={event => setTechnicianId(event.target.value)}>
                <option value="">—</option>
                {(equipments.technicians || []).map(worker => <option key={worker.id} value={worker.id}>{worker.name}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>{t("modal.date_planned")}</label>
              <input type="date" className={INPUT} value={datePlanned} onChange={event => setDatePlanned(event.target.value)} />
            </div>
          </div>
        )}
      </div>

      {error && <div className="mt-3 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">{error}</div>}
    </Modal>
  )
}

const MaterialRow = ({ material }) => (
  <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_120px_120px_minmax(0,1.2fr)] gap-3 items-center px-5 py-2 text-[12.5px]">
    <div className="min-w-0">
      <a href={material.path} className="text-slate-900 font-medium hover:underline truncate block" title={material.name}>{material.name}</a>
      <div className="text-[11px] text-slate-400">{t("material.open")}</div>
    </div>
    <div className="text-slate-600 tabular-nums truncate">{material.serial_number || <span className="text-slate-300">—</span>}</div>
    <div className="text-slate-600 tabular-nums">{material.in_service_date ? dateFmt(material.in_service_date) : "—"}</div>
    <div className="text-slate-600 tabular-nums">{material.warranty_date ? dateFmt(material.warranty_date) : "—"}</div>
    <div className="min-w-0 flex items-center gap-2 justify-end">
      {material.workorder ? (
        <>
          <a href={material.workorder.path} className="text-blue-700 hover:underline truncate" title={material.workorder.name}>{t("material.workorder")}</a>
          <a href={material.workorder.report_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 shrink-0">
            <FontAwesomeIcon icon="file-pdf" className="w-3 h-3" />{t("material.report")}
          </a>
        </>
      ) : (
        <span className="text-slate-400">{t("material.no_report")}</span>
      )}
    </div>
  </div>
)

const EquipmentsSection = ({ equipments, onChange }) => {
  const data = equipments || { rows: [], totals: {}, technicians: [] }
  const [installing, setInstalling] = useState(null)
  const [expanded, setExpanded]     = useState(() => new Set())
  const [notice, setNotice]         = useState(null)

  const toggle = id => setExpanded(previous => {
    const next = new Set(previous)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const saved = response => {
    setNotice(t("modal.saved", { count: response?.materials?.length || 0 }))
    onChange && onChange()
  }

  return (
    <section id="section-equipments" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3">
        <h2 className="text-[15px] font-semibold text-slate-900">{t("title")}</h2>
        <p className="text-[12.5px] text-slate-500 mt-0.5">{t("subtitle")}</p>
      </header>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Kpi label={t("kpi.to_install")} value={Math.round(data.totals?.to_install || 0)} />
        <Kpi label={t("kpi.installed")}  value={data.totals?.installed || 0} tone="text-emerald-700" />
        <Kpi label={t("kpi.reports")}    value={data.totals?.reports || 0}   tone="text-blue-700" />
      </div>

      {notice && (
        <div className="mb-3 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-[12.5px] flex items-center justify-between">
          <span><FontAwesomeIcon icon="check" className="mr-1.5" />{notice}</span>
          <button type="button" className="text-emerald-700 hover:text-emerald-900" onClick={() => setNotice(null)}><FontAwesomeIcon icon="xmark" /></button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.1fr)_60px_140px_150px_170px] gap-4 px-5 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
          <div>{t("columns.equipment")}</div>
          <div>{t("columns.lot")}</div>
          <div className="text-right">{t("columns.quantity")}</div>
          <div className="text-right">{t("columns.supply")}</div>
          <div>{t("columns.installed")}</div>
          <div />
        </div>

        {data.rows.length === 0 ? (
          <div className="px-6 py-12 text-center text-[13px] text-slate-500">{t("empty")}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.rows.map(row => {
              const done    = row.installed_count >= row.quantity && row.quantity > 0
              const percent = row.quantity > 0 ? Math.min(100, Math.round((row.installed_count / row.quantity) * 100)) : 0
              const open    = expanded.has(row.qline_id)
              return (
                <div key={row.qline_id}>
                  <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.1fr)_60px_140px_150px_170px] gap-4 px-5 py-3 items-center text-[12.5px]">
                    <div className="min-w-0">
                      <div className="text-slate-900 font-medium truncate" title={row.product?.name}>{row.product?.name}</div>
                      {row.product?.brand && <div className="text-[11px] text-slate-400 truncate">{row.product.brand}</div>}
                    </div>
                    <div className="min-w-0 text-slate-600 truncate">{row.lot?.title || <span className="text-slate-400">—</span>}</div>
                    <div className="text-right tabular-nums text-slate-700">{Math.round(row.quantity)}</div>
                    <div className="text-right tabular-nums text-slate-600">
                      {Math.round(row.ordered_quantity)} · {Math.round(row.received_quantity)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[12px] tabular-nums font-medium ${done ? "text-emerald-700" : row.installed_count > 0 ? "text-amber-700" : "text-slate-400"}`}>
                          {row.installed_count} / {Math.round(row.quantity)}
                        </span>
                        {done && <Badge tone="emerald" variant="soft" size="sm">{t("kpi.installed")}</Badge>}
                      </div>
                      <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${done ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      {row.installed_count > 0 && (
                        <button type="button" onClick={() => toggle(row.qline_id)} className="px-2.5 py-1.5 rounded-lg text-[12.5px] text-slate-500 hover:bg-slate-100">
                          {open ? t("actions.collapse") : t("actions.details")}
                        </button>
                      )}
                      {data.can_manage && !done && (
                        <Button variant="primary" size="sm" onClick={() => setInstalling(row)}>
                          <FontAwesomeIcon icon="plug-circle-check" className="mr-1.5" />{t("actions.install")}
                        </Button>
                      )}
                    </div>
                  </div>
                  {open && row.materials.length > 0 && (
                    <div className="bg-slate-50/60 border-t border-slate-100 divide-y divide-slate-100">
                      <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_120px_120px_minmax(0,1.2fr)] gap-3 px-5 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                        <div>{t("columns.equipment")}</div>
                        <div>{t("material.serial")}</div>
                        <div>{t("material.in_service")}</div>
                        <div>{t("material.warranty")}</div>
                        <div className="text-right">{t("material.report")}</div>
                      </div>
                      {row.materials.map(material => <MaterialRow key={material.id} material={material} />)}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {installing && (
        <InstallModal row={installing} equipments={data} onClose={() => setInstalling(null)} onSaved={saved} />
      )}
    </section>
  )
}

export default EquipmentsSection
